import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Image } from 'components/Image/Image';
import { showModal } from 'modules/main/store/ducks/modal';
import { subscribeToSubsite } from 'modules/main/store/ducks/subscribedSubsites';
import { useMainDispatch } from 'modules/main/store/hooks/useMainDispatch';
import { useMainSelector } from 'modules/main/store/hooks/useMainSelector';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { getStaticImageSrc } from 'utils/getImageSrc';
import { getParam } from 'utils/url';

type UseCmsSubscribeToSubsiteResult = {
  subscribe: () => Promise<void>;
  isSubscribed: boolean;
  submitting: boolean;
};

export function useCmsSubscribeToSubsite(
  subsiteId: string,
): UseCmsSubscribeToSubsiteResult {
  const dispatch = useMainDispatch();
  const location = useLocation();
  const subscribedSubsites = useMainSelector(
    (state) => state.subscribedSubsites,
  );
  const isSubscribed = subscribedSubsites.subsiteIds.includes(subsiteId);
  const [submitting, setSubmitting] = useState(false);
  const { user, isUserLoaded } = useUserEnvironment();

  const subscribe = useCallback(() => {
    if (user) {
      setSubmitting(true);
      return dispatch(subscribeToSubsite(subsiteId)).then(() => {
        setSubmitting(false);
      });
    }
    return Promise.resolve(
      dispatch(
        showModal('EMAIL_SIGNUP', {
          subscribedSubsite: subsiteId,
          title: (
            <div style={{ textAlign: 'center' }}>
              {getText('Sign up to subscribe to our newsletter!')}
            </div>
          ),
          image: (
            <div style={{ textAlign: 'center' }}>
              <Image src={getStaticImageSrc('subscribe-signup.svg')} alt="" />
            </div>
          ),
        }),
      ),
    ).then(() => {
      setSubmitting(false);
    });
  }, [user, dispatch, subsiteId]);

  useEffect(() => {
    if (isUserLoaded) {
      const initSubscribe = getParam(location, 'subscribe');

      if (initSubscribe) {
        subscribe();
      }
    }
  }, [isUserLoaded, location, subscribe]);

  return {
    subscribe,
    isSubscribed,
    submitting,
  };
}
